import { graphql, useStaticQuery } from "gatsby"
import _ from "lodash"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useState } from "react"
// import { geocodeByLatLng } from "react-google-places-autocomplete"
import { convertDistance, getDistance } from "geolib"
// import currentLocationIcon from "images/currentLocation_icon.svg"
import AddressCard from "components/blocks/addressCard"
import Map from "components/elements/map"
import PlacesAutocomplete from "components/elements/placesAutocomplete"
import RichText from "components/elements/richText"
import { useStaticQueryGetClinicsAddresses } from "services/graphQl/queries/clinicsAddresses"
import { googleMapURL } from "utils/constants"
import "./locations.scss"
const GetClinicSlugPrefix = () => {
  const query = useStaticQuery(
    graphql`
      query ClinicSlugPrefix {
        template: contentfulWebPageTemplate(model: { eq: "Clinic" }) {
          slugPrefix
        }
      }
    `
  )
  return query.template.slugPrefix
}

const LocationsPage = ({ additionalSettings, additionalText, location }) => {
  const allClinics = _.sortBy(useStaticQueryGetClinicsAddresses(), ["name"])
  const [clinics, setClinics] = useState(allClinics)
  const [closestCenter, setClosestCenter] = useState("")
  const [place, setPlace] = useState("")
  const [mapCenter, setMapCenter] = useState(null)
  const [mapZoom, setMapZoom] = useState(null)
  const [closeButton, setCloseButton] = useState(false)
  const [isMapView, setIsMapView] = useState(false)
  const [filteredState, setFilteredState] = useState("")

  const restoreClinics = useCallback(() => {
    setClinics(allClinics)
    setClosestCenter("")
    setFilteredState("")
    setPlace("")
    setCloseButton(false)
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }, [allClinics])

  const handleChange = useCallback(
    (geoCoor, fullPlace) => {
      if (!geoCoor || !fullPlace) {
        restoreClinics()
        return
      }

      const stateMatchedClinics = allClinics.filter(clinic =>
        fullPlace.toLowerCase().includes(clinic.state.toLowerCase())
      )

      if (stateMatchedClinics.length > 0) {
        setClinics(stateMatchedClinics)
        setCloseButton(true)
        setClosestCenter("")
        setFilteredState(stateMatchedClinics[0].state)
      } else {
        let minimumClinic = []
        let minimum = 200

        for (let i = 0; i < allClinics.length; i++) {
          const distance = convertDistance(
            getDistance(
              { latitude: geoCoor.lat, longitude: geoCoor.lng },
              {
                latitude: allClinics[i].address.lat,
                longitude: allClinics[i].address.lon,
              }
            ),
            "mi"
          )

          if (distance <= minimum) {
            minimumClinic = [allClinics[i]]
            minimum = distance
          }
        }

        setClinics(minimumClinic)
        setCloseButton(true)
        setFilteredState("")
        setClosestCenter(Math.round(minimum))
      }
    },
    [allClinics, restoreClinics]
  )

  const handleZoomChange = zoom => {
    setMapZoom(zoom)
  }

  const handleMarkerClick = locationIndex => {
    setClinics([allClinics[locationIndex]])
    setClosestCenter("")
    setPlace("")
    setCloseButton(false)
  }

  // Handle URL parameters on component mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search)
      const addressParam = urlParams.get("address")

      if (addressParam) {
        const decodedAddress = decodeURIComponent(addressParam)
        setPlace(decodedAddress)

        // Use Google Geocoding API to get coordinates from address
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder()
          geocoder.geocode({ address: decodedAddress }, (results, status) => {
            if (status === "OK" && results[0]) {
              const geoCoor = {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
              }
              // Call handleChange directly without causing re-render issues
              handleChange(geoCoor, decodedAddress)
            }
          })
        }
      }
    }
  }, []) // Remove handleChange from dependencies to prevent re-running

  useEffect(() => {
    if (clinics.length === 1) {
      setMapCenter({
        lat: clinics[0].address.lat,
        lng: clinics[0].address.lon,
      })
      setMapZoom(9)
    } else {
      setMapCenter(null)
      setMapZoom(null)
    }
  }, [clinics])
  //commented for future reference
  // const getCurrentLocation = () => {
  //   navigator.geolocation.getCurrentPosition(function (position) {
  //     const geoCoor = {
  //       lat: position.coords.latitude,
  //       lng: position.coords.longitude,
  //     }

  //     geocodeByLatLng(geoCoor)
  //       .then(results => setPlace(results[0].formatted_address))
  //       .catch(error => console.error(error))

  //     handleChange(geoCoor)
  //   })
  // }

  const renderClinics = () => {
    return (
      <>
        {clinics.length < allClinics.length && closestCenter && (
          <div className="closest-center-wrapper">
            <h4>Closest Center</h4>
            <p>({closestCenter} miles)</p>
          </div>
        )}
        {filteredState && (
          <div className="state-title">
            <h3>{filteredState}</h3>
          </div>
        )}
        <div className={isMapView ? "map-view-scroll" : "row"}>
          {clinics.map((clinic, index) => {
            const address = {
              ...clinic,
              slug: `/${GetClinicSlugPrefix()}/${clinic.slug}`,
            }

            return (
              <div
                className={
                  isMapView ? "map-view-item" : "col-lg-4 col-md-6 col-sm-12"
                }
                key={index}
              >
                <AddressCard
                  {...address}
                  enableDirectionsCTA={additionalSettings?.enableDirectionsCTA}
                  viewMode={isMapView ? "map" : "list"}
                />
              </div>
            )
          })}
        </div>
      </>
    )
  }

  const markerPositions = allClinics.map(item => {
    item.address.showInfo = false
    item.address.info = item.name
    return item.address
  })
  return (
    <div className="Locations">
      <div id="content">
        <div className="container">
          <div className="locations-head-wrapper">
            <div className="left-section">
              <PlacesAutocomplete
                closeButton={closeButton}
                defaultValue={place}
                googleMapURL={googleMapURL}
                key={place} // Force re-mount when place changes
                loadingElement={<div style={{ height: "100%" }} />}
                placeChanged={handleChange}
                setValue={setPlace}
              />
            </div>

            <div className="right-section">
              <div
                className={`map-list-switch ${
                  isMapView ? "map-active" : "list-active"
                }`}
              >
                <div className="switch-indicator" />
                <button
                  className={`switch-button ${!isMapView ? "active" : ""}`}
                  onClick={() => setIsMapView(false)}
                >
                  List
                </button>
                <button
                  className={`switch-button ${isMapView ? "active" : ""}`}
                  onClick={() => setIsMapView(true)}
                >
                  Map
                </button>
              </div>
            </div>
          </div>
          <div className="locations-wrapper">
            {isMapView && filteredState && (
              <div className="map-view-state-title">
                <h3>{filteredState}</h3>
              </div>
            )}
            {isMapView ? (
              <div className="map-view-wrapper">
                <div className="clinic-list-scrollable">
                  <div className="clinic-list">
                    {clinics.map((clinic, index) => {
                      const address = {
                        ...clinic,
                        slug: `/${GetClinicSlugPrefix()}/${clinic.slug}`,
                      }

                      return (
                        <AddressCard
                          key={index}
                          {...address}
                          enableDirectionsCTA={
                            additionalSettings?.enableDirectionsCTA
                          }
                          viewMode={isMapView ? "map" : "list"}
                        />
                      )
                    })}
                  </div>
                </div>

                <div className="map-view">
                  <Map
                    containerElement={
                      <div style={{ height: "400px", marginBottom: "30px" }} />
                    }
                    googleMapURL={googleMapURL}
                    handleMarkerClick={handleMarkerClick}
                    isMarkerShown
                    key={isMapView ? "mapView" : "listView"}
                    loadingElement={<div style={{ height: "100%" }} />}
                    mapCenter={mapCenter}
                    mapElement={<div style={{ height: "100%" }} />}
                    mapZoom={mapZoom}
                    markerPosition={markerPositions}
                    zoomChange={handleZoomChange}
                  />
                </div>
              </div>
            ) : clinics.length > 0 ? (
              renderClinics()
            ) : (
              additionalText && (
                <RichText richText={additionalText} textStyle="full" />
              )
            )}

            {/* commented for future reference
            <div className="locations-head-wrapper">
              <div className="head-right-side">
                <button
                  className="head-current-location"
                  onClick={getCurrentLocation}
                >
                  <span className="head-current-location-title">
                    Use Current Location
                  </span>
                  <img alt="Current Location" src={currentLocationIcon} />
                </button>
              </div>
            </div> */}

            {clinics.length < allClinics.length && (
              <div className="see-all-locations-wrapper">
                <button
                  className="see-all-locations-btn"
                  onClick={restoreClinics}
                >
                  See All Locations
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const LocationsPageProps = {
  additionalSettings: PropTypes.shape({
    enableDirectionsCTA: PropTypes.bool,
    testimonialsCount: PropTypes.number,
  }),
  additionalText: PropTypes.shape({
    json: PropTypes.object,
  }),
  location: PropTypes.object,
}

LocationsPage.propTypes = LocationsPageProps

export default LocationsPage
